/**
 * Zentrale Sprachliste des Webs (Teil 2 „international“, 31.10.2026) – Spiegel von
 * dartcloud-backend/src/core/i18n/supported-languages.js (Reihenfolge, Labels, Locales 1:1).
 * Rein (kein i18next, kein DOM) – darf von api.ts, Services und Tests importiert werden.
 *  - SUPPORTED_LANGS: Sprachen, für die es Bundles/Katalogfelder gibt.
 *  - DEFAULT_ACTIVE_LANGS: Startwert des Umschalters, bis /api/i18n/languages antwortet.
 * Fallback-Kette: Zielsprache → en → de.
 */
export type Lang = 'de' | 'en' | 'pl' | 'hu' | 'fr' | 'it' | 'nl';
export const SUPPORTED_LANGS: Lang[] = ['de', 'en', 'pl', 'hu', 'fr', 'it', 'nl'];
export const DEFAULT_LANG: Lang = 'de';
export const DEFAULT_ACTIVE_LANGS: Lang[] = ['de', 'en'];

/** Eigenbezeichnung je Sprache (so stehen sie im Umschalter). */
export const LANG_LABELS: Record<Lang, string> = {
  de: 'Deutsch', en: 'English', pl: 'Polski', hu: 'Magyar', fr: 'Français', it: 'Italiano', nl: 'Nederlands',
};

/** Locale für Datums-/Zahlenformate je Sprache. */
/** Flagge je Sprache (Dialog „Sprache & Länder“, 01.11.2026) – Englisch mit 🇬🇧 wie en-GB. */
export const LANG_FLAGS: Record<Lang, string> = { de: '🇩🇪', en: '🇬🇧', pl: '🇵🇱', hu: '🇭🇺', fr: '🇫🇷', it: '🇮🇹', nl: '🇳🇱' };
export const LANG_LOCALES: Record<Lang, string> = {
  de: 'de-DE', en: 'en-GB', pl: 'pl-PL', hu: 'hu-HU', fr: 'fr-FR', it: 'it-IT', nl: 'nl-NL',
};

/** 'de-DE' → 'de', 'PL_pl' → 'pl', Unbekanntes → null (pure). */
export const normalizeLang = (value: unknown): Lang | null => {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().split(/[-_;,]/)[0];
  return (SUPPORTED_LANGS as string[]).includes(code) ? (code as Lang) : null;
};

/** Aktive Sprachen bereinigen (nur unterstützte, de immer dabei, Reihenfolge wie SUPPORTED_LANGS). */
export const sanitizeActiveLangs = (codes: unknown): Lang[] => {
  // keine Liste (kaputter Cache, fehlende Server-Antwort) → Standard de,en
  if (!Array.isArray(codes)) return [...DEFAULT_ACTIVE_LANGS];
  const set = new Set<Lang>([DEFAULT_LANG]);
  for (const c of codes) { const l = normalizeLang(typeof c === 'object' && c ? (c as any).code : c); if (l) set.add(l); }
  const out = SUPPORTED_LANGS.filter((l) => set.has(l));
  return out.length ? out : [...DEFAULT_ACTIVE_LANGS];
};

/** Fallback-Kette für Texte: Zielsprache → en → de (ohne Dubletten). */
export const fallbackChain = (lang: unknown): Lang[] => {
  const chain: Lang[] = [];
  for (const l of [normalizeLang(lang) ?? DEFAULT_LANG, 'en' as Lang, DEFAULT_LANG]) if (!chain.includes(l)) chain.push(l);
  return chain;
};

/** Gerätesprache beim Erststart: unterstützt UND aktiv → diese, sonst en (de bleibt de). */
export const defaultLangForDevice = (deviceLocale: unknown, active: Lang[] = DEFAULT_ACTIVE_LANGS): Lang => {
  const l = normalizeLang(deviceLocale);
  if (l && active.includes(l)) return l;
  return active.includes('en') ? 'en' : DEFAULT_LANG;
};

/** Locale für Intl-Formatierung einer Sprache ('pl' → 'pl-PL', unbekannt → de-DE). */
export const localeFor = (lang: unknown): string => LANG_LOCALES[normalizeLang(lang) ?? DEFAULT_LANG];

/**
 * Sprachen mit URL-Präfix (International-SEO 01.11.2026): alle außer Deutsch – Deutsch bleibt ohne Präfix
 * (/loesungen/…), jede andere Sprache liegt unter /<lang>/… (/en/loesungen/…, /pl/ratgeber/…).
 */
export const URL_PREFIX_LANGS: Lang[] = SUPPORTED_LANGS.filter((l) => l !== DEFAULT_LANG);

/** og:locale-Form ('pl' → 'pl_PL'); unbekannt → de_DE. */
export const ogLocaleFor = (lang: unknown): string => localeFor(lang).replace('-', '_');
